import { SubtitlesGroup } from '../../../store/slices/subtitles/types';
import { UnauthorizedError } from '../../errors/UnauthorizedError';

import type {
  Compliance,
  ComplianceParserOptions,
  IPlayerConfigResponse,
  IPlayerConfigResponseXML,
  ParserOptions,
  PlayerConfigType,
} from './types';

export const parsePlayerConfigResponse = (
  response: IPlayerConfigResponse,
  options: ParserOptions
): PlayerConfigType => {
  return {
    netWinBalanceMap: response.netWinBalanceMap,
    jwtToken: response.jwtToken || '',
    userId: response.userId,
    countryCode: response.playerIPCountry,
    screenName: response.screenName,
    operatorId: response.operatorId,
    userType: response.userType,
    jurisdiction: response.jurisdiction,
    compliance: parseCompliance({
      jurisdiction: response.jurisdiction,
      dedicatedLanguage: options.dedicatedLanguage,
      isCrashGame: options.isCrashGame,
    }),
    videoVolume: parseParamNumber(response.params.video_sounds, 1, (value: number) => value / 100),
    gameEffectsVolume: parseParamNumber(response.params.game_effects, 1, (value: number) => value / 100),
    backgroundMusicVolume: parseParamNumber(response.params.bg_music, 1, (value: number) => value / 100),
    voiceOverVolume: parseParamNumber(response.params.vo_music, 1, (value: number) => value / 100),
    videoStream: parseGameIdString(response.params.video_stream, true, {
      gameId: options.operatorGameId,
      // Legacy game versions enable video stream for value `false` and disable for `true`.
      // Keeping inverse logic here to avoid breaking player config on the server.
      valueTransformer: (value: string) => value === 'false',
    }),
    selectedChipIndex: parseGameIdString(response.params.selected_chip, undefined, {
      gameId: options.operatorGameId,
      valueTransformer: parseInt,
    }),
    videoStreamRaw: response.params.video_stream ? { video_stream: response.params.video_stream } : undefined,
    selectedChipParamsRaw: response.params.selected_chip ? { selected_chip: response.params.selected_chip } : undefined,
    freeChipsParamsRaw: response.params.selected_chip_fb
      ? { selected_chip_fb: response.params.selected_chip_fb }
      : undefined,
    showOtherMessages: response.params.showothermessages,
    subtitlesEnabled: parseSubtitlesEnabledGroups((group) => response.params[`sub_enabled_${group}`]),
    subtitlesOnboarded: response.params.sub_onboarded ?? false,
    subtitlesPositionsRaw: response.params.sub_positions ?? '',
    subtitlesSize: response.params.sub_sizes ?? '',
    currencyMultiplier: response.currencyMultiplier ? Number(response.currencyMultiplier) : 1,
    lobbyFilters: parseLobbyFilters(response.params.lobby_filters),
    isBanned: response.params.isBanned ? response.params.isBanned : false,
    isSecondBetboardEnabled: response.params.issecondbetboardenabled ? response.params.issecondbetboardenabled : true,
    hfAnimationEnabled: response.params.hf_animation_enabled ? response.params.hf_animation_enabled : true,
    lobbyFavourites: parseLobbyFavourites(response.params.lobbyfavourites),
    bcBonusBetShow: response.params.bcbonusbetshow ?? true,
    bcPairBets: response.params.bcpairbets ?? true,
    bcLightTheme: response.params.bclighttheme ?? true,
    bcHasNoCommission: response.params.isnocommission ?? true,
    decisionstrategystatus: response.params.decisionstrategystatus,
    autostand: response.params.autostand,
    autohit: response.params.autohit,
    autoinsurance: response.params.autoinsurance,
    splitMinRange: response.params.showdec_split_min,
    splitMaxRange: response.params.showdec_split_max,
    doubleMinRange: response.params.showdec_double_min,
    doubleMaxRange: response.params.showdec_double_max,
    betBehindSplit: response.params.betbehind_split,
    betBehindDouble: response.params.betbehind_doubledown,
    betBehindInsurance: response.params.betbehind_insurance,
    bcShowBettingStats: response.params.showbettingstats ?? true,
    autoplayOnboarded: response.params.autoplay_onboarded ?? false,
    hfHideMyBets: response.params.hfhidemybets ?? false,
  };
};

export const parsePlayerConfigResponseXML = (
  response: IPlayerConfigResponseXML,
  { dedicatedLanguage, operatorGameId, isCrashGame }: ParserOptions
): PlayerConfigType => {
  const {
    user_id,
    screenName,
    operator_id,
    user_type,
    Jurisdiction,
    player_ip_country,
    jwtToken,
    currency_multiplier,
    'playerconfig-param': playerConfigParamsRaw,
    nwb,
    session,
  } = response;

  if (session === 'offline') {
    throw new UnauthorizedError('parsePlayerConfigResponseXML');
  }
  // In some cases player config params may be sent as a single value
  const playerConfigParams = Array.isArray(playerConfigParamsRaw) ? playerConfigParamsRaw : [playerConfigParamsRaw];

  const compliance = parseCompliance({
    jurisdiction: Jurisdiction,
    dedicatedLanguage,
    isCrashGame,
  });

  const videoStream: boolean = parseParam(
    playerConfigParams,
    'video_stream',
    true,
    operatorGameId,
    // Legacy game versions enable video stream for value `false` and disable for `true`.
    // Keeping inverse logic here to avoid breaking player config on the server.
    (value: string) => value === 'false'
  );

  const videoVolume: number = parseParam(
    playerConfigParams,
    'video_sounds',
    1,
    undefined,
    (value: string) => parseInt(value) / 100
  );
  const decisionstrategystatus: string = parseParam(
    playerConfigParams,
    'decisionstrategystatus',
    '0',
    undefined,
    (value: string) => value
  );
  const autostand: string = parseParam(playerConfigParams, 'autostand', '0', undefined, (value: string) => value);
  const autohit: string = parseParam(playerConfigParams, 'autohit', '0', undefined, (value: string) => value);
  const autoinsurance: string = parseParam(
    playerConfigParams,
    'autoinsurance',
    '0',
    undefined,
    (value: string) => value
  );
  const splitMinRange: string = parseParam(
    playerConfigParams,
    'showdec_split_min',
    '0',
    undefined,
    (value: string) => value
  );
  const splitMaxRange: string = parseParam(
    playerConfigParams,
    'showdec_split_max',
    '0',
    undefined,
    (value: string) => value
  );
  const doubleMinRange: string = parseParam(
    playerConfigParams,
    'showdec_double_min',
    '0',
    undefined,
    (value: string) => value
  );
  const doubleMaxRange: string = parseParam(
    playerConfigParams,
    'showdec_double_max',
    '0',
    undefined,
    (value: string) => value
  );

  const betBehindSplit: boolean | null = parseParam(
    playerConfigParams,
    'betbehind_split',
    null,
    undefined,
    (value: string) => value === 'true'
  );
  const betBehindDouble: boolean | null = parseParam(
    playerConfigParams,
    'betbehind_doubledown',
    null,
    undefined,
    (value: string) => value === 'true'
  );
  const betBehindInsurance: boolean | null = parseParam(
    playerConfigParams,
    'betbehind_insurance',
    null,
    undefined,
    (value: string) => value === 'true'
  );

  const gameEffectsVolume: number = parseParam(
    playerConfigParams,
    'game_effects',
    1,
    undefined,
    (value: string) => parseInt(value) / 100
  );

  const backgroundMusicVolume: number = parseParam(
    playerConfigParams,
    'bg_music',
    1,
    undefined,
    (value: string) => parseInt(value, 10) / 100
  );

  const voiceOverVolume: number = parseParam(
    playerConfigParams,
    'vo_music',
    1,
    undefined,
    (value: string) => parseInt(value, 10) / 100
  );

  const selectedChipIndex: number | undefined = parseParam(
    playerConfigParams,
    'selected_chip',
    undefined, // fallback to default_chip_order from tableConfig
    operatorGameId,
    parseInt
  );

  const isBanned = parseParam(playerConfigParams, 'isBanned', false, undefined, (value: string) => {
    return value === 'true';
  });
  const netWinBalanceMap: Record<string, number> = nwb ? parseNetWinBalanceString(nwb) : {};

  const lobbyFilters = parseParamLobbyFilters(playerConfigParams, 'lobby_filters');

  const currencyMultiplier: number = currency_multiplier ? Number(currency_multiplier) : 1;

  const videoStreamRaw = playerConfigParams?.find((item) => 'video_stream' in item);
  const freeChipsParamsRaw = playerConfigParams?.find((item) => 'selected_chip_fb' in item);
  const selectedChipParamsRaw = playerConfigParams?.find((item) => 'selected_chip' in item);
  const showOtherMessages: boolean = parseParam(
    playerConfigParams,
    'showothermessages',
    true,
    undefined,
    (value: string) => value === 'true'
  );
  const subtitlesEnabled = parseSubtitlesEnabledGroups((group) => {
    return parseParam(
      playerConfigParams,
      `sub_enabled_${group}`,
      undefined,
      undefined,
      (value: string) => value === 'true'
    );
  });
  const subtitlesOnboarded = parseParam(
    playerConfigParams,
    'sub_onboarded',
    false,
    undefined,
    (value: string) => value === 'true'
  );
  const subtitlesPositionsRaw = parseStringForJson(getValueByKey(playerConfigParams, 'sub_positions'));
  const subtitlesSize = parseStringForJson(getValueByKey(playerConfigParams, 'sub_sizes'));
  const autoplayOnboarded = parseParam(
    playerConfigParams,
    'autoplay_onboarded',
    false,
    undefined,
    (value: string) => value === 'true'
  );
  const isSecondBetboardEnabled: boolean = parseParam(
    playerConfigParams,
    'issecondbetboardenabled',
    true,
    undefined,
    (value: string) => value === 'true'
  );
  const hfAnimationEnabled: boolean = parseParam(
    playerConfigParams,
    'hf_animation_enabled',
    true,
    undefined,
    (value: string) => value === 'true'
  );

  const getAllBCPropValue = (): Record<string, boolean> => {
    const BC_PLAYER_PROPS = ['bcbonusbetshow', 'bcpairbets', 'isnocommission', 'showbettingstats'];
    const returnPlayerProps = ['bcBonusBetShow', 'bcPairBets', 'bcHasNoCommission', 'bcShowBettingStats'];
    const returnObj: Record<string, boolean> = {};
    return BC_PLAYER_PROPS.reduce((acc, eachProp, ind) => {
      const val = parseParam(playerConfigParams, eachProp, true, undefined, (value: string) => value === 'true');
      acc[returnPlayerProps[ind] as string] = val;
      return acc;
    }, returnObj);
  };
  const bcLightTheme: boolean = parseParam(
    playerConfigParams,
    'bclighttheme',
    true,
    undefined,
    (value: string) => value === 'true'
  );
  const hfHideMyBets: boolean = parseParam(
    playerConfigParams,
    'hfhidemybets',
    false,
    undefined,
    (value: string) => value === 'true'
  );
  const { bcBonusBetShow, bcPairBets, bcHasNoCommission, bcShowAllSideBets, bcShowBettingStats } = getAllBCPropValue();

  const lobbyFavourites = parseLobbyFavourites(getValueByKey(playerConfigParams, 'lobbyfavourites'));

  return {
    jwtToken,
    userId: user_id,
    countryCode: player_ip_country,
    screenName,
    operatorId: operator_id,
    userType: user_type,
    jurisdiction: Jurisdiction,
    compliance,
    videoVolume,
    gameEffectsVolume,
    videoStream,
    selectedChipIndex,
    videoStreamRaw,
    selectedChipParamsRaw,
    showOtherMessages,
    subtitlesEnabled,
    subtitlesOnboarded,
    subtitlesPositionsRaw,
    subtitlesSize,
    currencyMultiplier,
    lobbyFilters,
    isBanned,
    netWinBalanceMap,
    isSecondBetboardEnabled,
    hfAnimationEnabled,
    lobbyFavourites,
    bcBonusBetShow,
    bcPairBets,
    bcHasNoCommission,
    bcShowBettingStats,
    bcShowAllSideBets,
    bcLightTheme,
    decisionstrategystatus,
    autostand,
    autohit,
    autoinsurance,
    splitMinRange,
    splitMaxRange,
    doubleMinRange,
    doubleMaxRange,
    backgroundMusicVolume,
    voiceOverVolume,
    betBehindSplit,
    betBehindDouble,
    betBehindInsurance,
    freeChipsParamsRaw,
    autoplayOnboarded,
    hfHideMyBets,
  };
};

export const getComplianceByJurisdiction = ({
  jurisdiction,
  dedicatedLanguage,
  isCrashGame,
}: ComplianceParserOptions): Compliance => {
  switch (jurisdiction) {
    case 'CH':
      return {
        maxPlayerLimitInfo: true,
      };
    case 'CO':
      return {
        creditHelpInfo: true,
      };
    case 'IT':
      return {
        waitResultBeforeLeave: true,
        sessionBalance: !isCrashGame,
        showSessionId: true,
        isRestrictedToLeaveWithActiveBet: true,
      };
    case 'ON':
      return {
        netPosition: true,
        showSessionTimer: true,
        hideAutoplay: true,
      };
    case 'LT':
    case 'NL':
      return {
        hideAutoplay: true,
      };
    case 'UK':
      return {
        extendedAutoplay: true,
        displayBonusBalance: true,
        netPosition: true,
        showSessionTimer: true,
      };
    case 'X1':
      return {
        extendedAutoplay: true,
      };
    case 'BR':
      return {
        disableStudioSound: dedicatedLanguage !== 'pt',
      };
    default:
      return {
        waitResultBeforeLeave: false,
        hideAutoplay: false,
        extendedAutoplay: false,
        showSessionTimer: false,
        showSessionId: false,
        displayBonusBalance: false,
        sessionBalance: false,
        creditHelpInfo: false,
        maxPlayerLimitInfo: false,
        netPosition: false,
        hideCurrency: false,
        isRestrictedToLeaveWithActiveBet: false,
        disableStudioSound: false,
      };
  }
};

export const parseCompliance = (options: ComplianceParserOptions): Compliance => {
  // TODO: make server send compliance as flags and replace this function
  const compliance = getComplianceByJurisdiction(options);

  // This is specific to HighFlyer PT table to hide AutoPlay.
  if (options.isCrashGame && options.dedicatedLanguage === 'pt') {
    compliance.hideAutoplay = true;
  }
  return compliance;
};

const gameRegEx = /^[^,=]+$/;
export const parseParam = <T>(
  playerConfigParams: Array<Record<string, string>> | null | undefined,
  substring: string,
  defaultValue: T,
  gameId?: string,
  valueTransformer?: (value: string) => T
): T => {
  const paramValue = playerConfigParams?.find((item) => substring in item)?.[substring];

  if (!paramValue) {
    return defaultValue;
  }

  if (!gameId) {
    /**
     * In the case of a single value string with no gameId, apply valueTransformer if provided.
     * Otherwise, return the singleValue as T.
     */
    const singleValue = gameRegEx.test(paramValue) ? paramValue : (defaultValue as string);
    return valueTransformer?.(singleValue) ?? (singleValue as T);
  }

  /**
   * Most param values are usually stored per game in a list, e.g. "1000=true,1100=true,1101=false".
   * Use RegExp to find value corresponding to provided gameId inside the list.
   */
  return parseGameIdString(paramValue, defaultValue, { gameId, valueTransformer });
};

export const parseParamNumber = <ValueType>(
  value: number | undefined,
  defaultValue: ValueType,
  valueTransformer?: (value: number) => ValueType
): ValueType => {
  if (value === undefined) return defaultValue;

  return valueTransformer?.(value) ?? (value as ValueType);
};

export const parseGameIdString = <ValueType>(
  string: string,
  defaultValue: ValueType,
  options?: {
    gameId?: string;
    valueTransformer?: (value: string) => ValueType;
  }
): ValueType => {
  const regex = new RegExp(`\\b${options?.gameId}=(\\w+)`);
  const matchGameId = regex.exec(string)?.[1];

  if (!matchGameId) {
    return defaultValue;
  }
  return options?.valueTransformer?.(matchGameId) ?? (matchGameId as ValueType);
};

const ampRegEx = /amp;/g;
const quotRegEx = /&quot;/g;
export const parseStringForJson = (value: string): string => {
  return value.replace(ampRegEx, '').replace(quotRegEx, '"');
};

/**
 * Parses lobby filters from player config response.
 * @param lobbyFilters - stringified JSON from player config response
 * @returns parsed lobby filters
 */
export const parseLobbyFilters = (lobbyFilters?: string): Record<string, Record<string, string[]>> => {
  if (!lobbyFilters) return {};
  const convertedToStringForJson = parseStringForJson(lobbyFilters);
  try {
    return JSON.parse(convertedToStringForJson);
  } catch (e) {
    /**
     * This error is intentionally swallowed because while playerConfig is essential, these lobby filters are not and have a default fallback.
     * If an ParsingError would be thrown here, it would stop lobby/game from working which in this case is not desired.
     */
    console.error('Error parsing lobby filters', convertedToStringForJson);
    return {};
  }
};

export const parseParamLobbyFilters = (
  playerConfigParams: Array<Record<string, string>> | undefined,
  substring: string
): Record<string, Record<string, string[]>> => {
  const paramValue = playerConfigParams?.find((item) => substring in item)?.[substring];

  if (!paramValue) {
    return {};
  }

  return parseLobbyFilters(paramValue);
};

export const getValueByKey = (playerConfigParams: Record<string, string>[], key: string): string => {
  const dataItem = playerConfigParams.find((item) => key in item);
  return dataItem?.[key] || '';
};

export const parseNetWinBalanceString = (nwbString: string): Record<string, number> => {
  const netWinBalanceObj: Record<string, number> = {};
  return nwbString.split(',').reduce((acc, cur) => {
    const [tableId, nwbValue] = cur.split('=');
    if (tableId) acc[tableId] = Number(nwbValue);
    return acc;
  }, netWinBalanceObj);
};

export const parseLobbyFavourites = (lobbyFavourites?: string | number): string[] => {
  switch (typeof lobbyFavourites) {
    case 'string':
      if (lobbyFavourites === '') return [];
      return lobbyFavourites.split(',');
    case 'number':
      return [lobbyFavourites.toString()];
    default:
      return [];
  }
};

const parseSubtitlesEnabledGroups = (
  callback: (group: SubtitlesGroup) => boolean | undefined
): Record<SubtitlesGroup, boolean> => {
  const subtitlesGroups = [SubtitlesGroup.GROUP1, SubtitlesGroup.GROUP2, SubtitlesGroup.GROUP3];
  return subtitlesGroups.reduce<Record<SubtitlesGroup, boolean>>(
    (result, group) => {
      result[group] = callback(group) ?? result[group];
      return result;
    },
    // Subtitles enabled groups with default values
    {
      [SubtitlesGroup.GROUP1]: false,
      [SubtitlesGroup.GROUP2]: true,
      [SubtitlesGroup.GROUP3]: false,
    }
  );
};
